import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import { Shift } from '../interfaces';
import { Property } from '../Toolbar';
import { formatPrice } from '../utils/formatPrice';
import './ZReportView.css';

interface ZReportViewProps {
    shift: Shift | null;
    property: Property | null;
    onSave: () => void;
    onEmail: () => void;
    onPrint: () => void;
    onNewShift: () => void;
    isUserReport?: boolean;
}

export interface ZReportViewRef {
    getReceiptElement: () => HTMLDivElement | null;
}

const ZReportView = forwardRef<ZReportViewRef, ZReportViewProps>(({
    shift,
    property,
    onSave,
    onEmail,
    onPrint,
    onNewShift,
    isUserReport
}, ref) => {
    const receiptRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(ref, () => ({
        getReceiptElement: () => receiptRef.current
    }));
    const formatNumber = (value: number) => {
        return formatPrice(value);
    };

    const formatDate = (date: Date | string | null | undefined) => {
        if (!date) return 'N/A';
        const d = typeof date === 'string' ? new Date(date) : date;
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        const hours = String(d.getHours()).padStart(2, '0');
        const minutes = String(d.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day} ${hours}:${minutes}`;
    };

    // Use shift dates
    const periodStart = shift?.startDate;
    const periodEnd = shift?.endDate ?? new Date().toISOString();

    // Sales data from shift entity
    const grossSales = shift?.grossSales ?? 0;
    const discounts = shift?.discounts ?? 0;
    const mealPlanDeductions = shift?.mealPlanDeductions ?? 0;
    const returns = shift?.returns ?? 0;
    const netSales = shift?.netSales ?? 0;
    const taxes = shift?.taxes ?? 0;
    const serviceCharge = shift?.serviceCharge ?? 0;
    const totalRevenue = shift?.totalRevenue ?? 0;

    // Receipts
    const numberOfReceipts = shift?.numberOfReceipts ?? 0;

    // Tenders
    const expectedCash = shift?.expectedCash ?? 0;
    const creditCards = shift?.creditCards ?? 0;
    const roomCharges = shift?.roomCharges ?? 0;

    // Drawer reconciliation
    const actualCashCount = shift?.actualCashCount ?? 0;
    const detectedVariance = shift?.detectedVariance ?? 0;

    // Bank deposit
    const nextShiftFloat = shift?.nextShiftFloat ?? 0;
    const bankDeposit = shift?.bankDeposit ?? 0;
    const bankBagNumber = shift?.bankBagNumber ?? '';

    // Controls
    const numberOfVoidOrders = shift?.numberOfVoidOrders ?? 0;
    const firstOrderNumber = shift?.firstOrderNumber ?? 'N/A';
    const lastOrderNumber = shift?.lastOrderNumber ?? 'N/A';

    // Cashier info
    const cashierName = shift?.closedByUserWithName ?? shift?.startedByUserWithName ?? 'N/A';

    return (
        <div className="zreport-page">
            <div className="zreport-success-banner">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span>Shift Closed Successfully</span>
            </div>

            <div className="zreport-container">
                <div className="zreport-receipt" ref={receiptRef}>
                    {/* Header */}
                    <div className="receipt-header">
                        <h2 className="company-name">{property?.displayName ?? 'PROPERTY NAME'}</h2>
                        <p className="company-details">
                            {/* {property?.address ?? ''} {property?.registerId ? `| Reg: ${property.registerId}` : ''} */}
                        </p>
                        <p className="cashier-info">Cashier: {cashierName}</p>
                    </div>

                    {/* Report Title */}
                    <div className="report-title">
                        <div className="title-main">{isUserReport ? 'USER Z REPORT' : 'Z REPORT'}</div>
                        <p>Z# {shift?.shiftId ?? '000000'}    {formatDate(periodEnd)}</p>
                        <p className="period">Period: {formatDate(periodStart)} → {formatDate(periodEnd)}</p>
                    </div>

                    {/* Sales Section */}
                    <div className="receipt-section">
                        <div className="section-header">SALES</div>
                        <div className="receipt-line">
                            <span>Gross Sales (excl tax)</span>
                            <span>{formatNumber(grossSales)}</span>
                        </div>
                        <div className="receipt-line">
                            <span>Discounts/Promos</span>
                            <span>-{formatNumber(discounts)}</span>
                        </div>
                        {mealPlanDeductions > 0 && (
                            <div className="receipt-line">
                                <span>Meal Plan Deductions</span>
                                <span>-{formatNumber(mealPlanDeductions)}</span>
                            </div>
                        )}
                        <div className="receipt-line">
                            <span>Returns/Refunds</span>
                            <span>-{formatNumber(returns)}</span>
                        </div>
                        <div className="receipt-line">
                            <span>Net Sales</span>
                            <span>{formatNumber(netSales)}</span>
                        </div>
                        <div className="receipt-line">
                            <span>Tax</span>
                            <span>{formatNumber(taxes)}</span>
                        </div>
                        <div className="receipt-line">
                            <span>Service Charge</span>
                            <span>{formatNumber(serviceCharge)}</span>
                        </div>
                        <div className="receipt-line total">
                            <span>Total Revenue</span>
                            <span>{formatNumber(totalRevenue)}</span>
                        </div>
                        <div className="receipt-line small">
                            <span>Total Receipts</span>
                            <span>{numberOfReceipts}</span>
                        </div>
                    </div>

                    {/* Floor Breakdown */}
                    {(shift as any)?.floorBreakdown?.length > 0 && (
                        <div className="receipt-section">
                            <div className="section-header">SALES BY FLOOR</div>
                            {(shift as any).floorBreakdown.map((floor: any, idx: number) => (
                                <div key={idx} style={{ marginBottom: 6 }}>
                                    <div className="receipt-line" style={{ fontWeight: 600 }}>
                                        <span>{floor.floorName}</span>
                                        <span>{formatNumber(floor.totalRevenue)}</span>
                                    </div>
                                    <div className="receipt-line small" style={{ paddingLeft: 10 }}>
                                        <span>Orders: {floor.orderCount}</span>
                                        <span>Gross: {formatNumber(floor.grossSales)}</span>
                                    </div>
                                    {floor.mealPlanDeductions > 0 && (
                                        <div className="receipt-line small" style={{ paddingLeft: 10 }}>
                                            <span>Meal Plan</span>
                                            <span>-{formatNumber(floor.mealPlanDeductions)}</span>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Tenders Section */}
                    <div className="receipt-section">
                        <div className="section-header">TENDERS</div>
                        <div className="receipt-line">
                            <span>Cash</span>
                            <span>{formatNumber(expectedCash)}</span>
                        </div>
                        <div className="receipt-line">
                            <span>Credit Card</span>
                            <span>{formatNumber(creditCards)}</span>
                        </div>
                        <div className="receipt-line">
                            <span>Room Charges</span>
                            <span>{formatNumber(roomCharges)}</span>
                        </div>
                    </div>

                    {/* Drawer Reconciliation Section */}
                    <div className="receipt-section">
                        <div className="section-header">DRAWER RECONCILIATION</div>
                        <div className="receipt-line">
                            <span>Expected Cash</span>
                            <span>{formatNumber(expectedCash)}</span>
                        </div>
                        <div className="receipt-line">
                            <span>Counted Cash</span>
                            <span>{formatNumber(actualCashCount)}</span>
                        </div>
                        <div className="receipt-line total">
                            <span>Over/Short</span>
                            <span className={detectedVariance < 0 ? 'negative' : ''}>{formatNumber(detectedVariance)}</span>
                        </div>
                    </div>

                    {/* Bank Deposit Section */}
                    <div className="receipt-section">
                        <div className="section-header">BANK DEPOSIT</div>
                        <div className="receipt-line">
                            <span>Left in Drawer (Next Float)</span>
                            <span>{formatNumber(nextShiftFloat)}</span>
                        </div>
                        <div className="receipt-line total">
                            <span>Total Deposit</span>
                            <span>{formatNumber(bankDeposit)}</span>
                        </div>
                        <div className="receipt-line small center">
                            <span>Bag #: {bankBagNumber || 'N/A'}</span>
                        </div>
                    </div>

                    {/* Controls Section */}
                    <div className="receipt-section">
                        <div className="section-header">CONTROLS</div>
                        <div className="controls-grid">
                            <span>Receipts: {numberOfReceipts}</span>
                            <span>Voids: {numberOfVoidOrders}</span>
                        </div>
                        <div className="receipt-line small center">
                            <span>First: {firstOrderNumber}</span>
                        </div>
                        <div className="receipt-line small center">
                            <span>Last: {lastOrderNumber}</span>
                        </div>
                    </div>

                    {/* Signature Section */}
                    <div className="signature-section">
                        <div className="signature-line">
                            <div className="signature-field">
                                <div className="signature-underline"></div>
                                <span>Cashier</span>
                            </div>
                            <div className="signature-field">
                                <div className="signature-underline"></div>
                                <span>Supervisor</span>
                            </div>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="receipt-footer">
                        <span>[Z COUNTERS RESET.]</span>
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="zreport-actions">
                    <button className="action-btn save-btn" onClick={onSave}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M19 21H5C3.89543 21 3 20.1046 3 19V5C3 3.89543 3.89543 3 5 3H16L21 8V19C21 20.1046 20.1046 21 19 21Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M17 21V13H7V21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M7 3V8H15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        Save
                    </button>
                    <button className="action-btn email-btn" onClick={onEmail}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M22 6L12 13L2 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        Email
                    </button>
                    <button className="action-btn print-btn" onClick={onPrint}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M6 9V2H18V9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M6 18H4C2.89543 18 2 17.1046 2 16V11C2 9.89543 2.89543 9 4 9H20C21.1046 9 22 9.89543 22 11V16C22 17.1046 21.1046 18 20 18H18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M18 14H6V22H18V14Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        Print
                    </button>
                    <button className="action-btn new-shift-btn" onClick={onNewShift}>
                        New Shift
                    </button>
                </div>
            </div>
        </div>
    );
});

export default ZReportView;